import CloseIcon from "@mui/icons-material/Close";
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import { useAppSelector } from "app/hooks";
import settings from "app/settings";
import { fetchAuthenticatedHtml } from "features/email-preview/emailPreviewApi";
import { selectAccessToken } from "features/identity/identitySlice";
import I18n from "i18n";
import { useEffect, useState } from "react";
import { BulkEmailSentItem } from "./bulkEmailSentApi";

interface BulkEmailSentPreviewDialogProps {
  item: BulkEmailSentItem | null;
  onClose: () => void;
}

export default function BulkEmailSentPreviewDialog({
  item,
  onClose,
}: BulkEmailSentPreviewDialogProps) {
  const accessToken = useAppSelector(selectAccessToken);
  const [html, setHtml] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    setHtml("");
    setError(false);
    if (!item) return;

    let active = true;
    setLoading(true);
    fetchAuthenticatedHtml(
      `${settings.apiHost}/bulk-emails-sent/${item.id}/preview`,
      accessToken!,
    )
      .then(content => {
        if (active) setHtml(content);
      })
      .catch(() => {
        if (active) setError(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [item, accessToken]);

  return (
    <Dialog open={!!item} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        {I18n.t("BulkEmailSent.previewTitle")}
        {item?.template_name ? ` - ${item.template_name}` : ""}
        <IconButton
          aria-label={I18n.t("App.close")}
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ height: "min(75vh, 800px)", p: 2 }}>
        <Alert severity="info" sx={{ mb: 2 }}>
          {I18n.t("BulkEmailSent.previewCurrentDataNotice")}
        </Alert>
        {loading && (
          <Box
            sx={{
              height: "calc(100% - 64px)",
              display: "grid",
              placeItems: "center",
            }}
          >
            <CircularProgress />
          </Box>
        )}
        {error && (
          <Alert severity="error">{I18n.t("BulkEmailSent.previewError")}</Alert>
        )}
        {!loading && !error && html && (
          <iframe
            title={I18n.t("BulkEmailSent.previewTitle")}
            srcDoc={html}
            sandbox="allow-same-origin"
            style={{ width: "100%", height: "calc(100% - 64px)", border: 0 }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
